/**
 * RailLeafChip — an individual WHERE/HAVING-tree leaf in the Rail view.
 * Read-only (no editContext): renders an inert mono chip identical to the
 * 3B.2 LeafChip. Editable (editContext present): click → inline input
 * (Enter/blur commits `modifyFilterNode`, Escape cancels) + `×` remove button.
 * Analytics fired on modify and remove.
 *
 * Slice 3B.3 — path-addressed WHERE edit.
 * Slice 3B.7 — when the leaf wraps an inline `IN (SELECT …)` / scalar subquery
 * and a `subquery` bundle is supplied, the leaf renders the shipped 3C drill
 * chips (via `renderInlineSubqueries`) in place of editable text, so a subquery
 * nested in an AND/OR group is drillable from the Rail. Works read-only and
 * editable; the remove affordance only appears with an `editContext`.
 *
 * HAVING parity (Plan 3 Task 5): `kind` (default `'where'`) parameterizes the
 * testids, the `feature:vqe_${kind}_node_edit` analytics, the drill label, and
 * threads `clause` into `modifyFilterNode`/`removeFilterNode`.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement, RefObject } from 'react';

import type { ParseOptions } from '../../../../sql-grammar/model';
import type { BooleanNode } from '../../../../sql-resolver/types';
import type { BooleanClauseTarget } from '../../../../sql-resolver/types/structuralEdit';
import type { VqeEditContext } from '../../edit/editContext';
import { toCommitCtx } from '../../edit/editContext';
import { modifyFilterNode } from '../../edit/edits/modifyFilterNode';
import { removeFilterNode } from '../../edit/edits/removeFilterNode';
import { sqlEngineToDatabaseEngineId } from '../../edit/livePullSchemaSnapshot';
import { buildDrillLabel, extractPredicateColumn } from '../../ux/drillLabel';
import { isWholeChipFilter, resolveEditPredicate } from '../common/looseFkEditEnrichment';
import { useVqeFilterColumns } from '../common/useVqeFilterColumns';
import { VqeFilterChipBuilder } from '../common/VqeFilterChipBuilder';
import { RemoveButton } from '../common/RemoveButton';
import { PillValueLabel } from './FilterPillLabels';
import { renderInlineSubqueries } from './renderInlineSubqueries';
import type { SubqueryWiring } from './SelectRenderer';
import { useAnalytics } from '../../../../../core/analytics/useAnalytics';

type LeafNode = BooleanNode & { kind: 'leaf' };

/** Subquery-drill context threaded to every Rail leaf (Slice 3B.7). */
export type RailSubquery = {
  readonly subqueryCtx: SubqueryWiring;
  readonly bufferText: string;
  readonly options: ParseOptions;
};

const BASE_CHIP =
  'inline-flex w-fit items-center gap-1.5 rounded-md border border-border bg-muted/40 px-2.5 py-1';

/**
 * Editing-mode body for an editable rail leaf — decides chip vs. raw-text
 * input using `resolveEditPredicate`, the SAME resolvability check
 * `VqeFilterChipBuilder.tsx`'s `EditBuilder` uses internally (PARITY-bundle
 * review fix), so this gate can never diverge from what the builder can
 * actually resolve.
 *
 * A genuinely separate component (not inlined in `RailLeafChip`): its
 * `useVqeFilterColumns` call needs a REAL `editContext`, but `RailLeafChip`'s
 * own `editContext` prop is optional (read-only rail leaves have none), so
 * that hook can't sit in `RailLeafChip`'s unconditional hook chain — a
 * conditional call there would violate the rules of hooks. This component is
 * only ever mounted from the branch where `editContext` is already known
 * non-null (mirrors the accepted `mode`-branch pattern in
 * `VqeFilterChipBuilder` itself: "branch picks one component type
 * consistently — no hook-order hazard").
 */
function RailLeafEditor({
  editContext,
  testIdPrefix,
  text,
  value,
  onValueChange,
  inputRef,
  onChipCommit,
  onChipCancel,
  onInputCommit,
  onInputCancel,
}: {
  readonly editContext: VqeEditContext;
  readonly testIdPrefix: string;
  readonly text: string;
  readonly value: string;
  readonly onValueChange: (next: string) => void;
  readonly inputRef: RefObject<HTMLInputElement>;
  readonly onChipCommit: (newText: string) => void;
  readonly onChipCancel: () => void;
  readonly onInputCommit: () => void;
  readonly onInputCancel: () => void;
}): ReactElement {
  const engine = sqlEngineToDatabaseEngineId(editContext.service.engineId);
  const cols = useVqeFilterColumns(editContext);
  const canEditAsChip = useMemo(
    () => resolveEditPredicate(text, engine, cols.resolveByField) !== null,
    [text, engine, cols.resolveByField],
  );

  if (canEditAsChip) {
    // Round-trippable predicate → structured chip editor.
    return (
      <VqeFilterChipBuilder
        mode="edit"
        testId={`${testIdPrefix}-chip`}
        editContext={editContext}
        initialPredicate={text}
        onCommit={onChipCommit}
        onCancel={onChipCancel}
      />
    );
  }
  // Non-round-trippable predicate → inline text input fallback.
  return (
    <input
      ref={inputRef}
      data-testid={`${testIdPrefix}-input`}
      className="bg-transparent font-mono text-[12px] outline-none"
      value={value}
      onChange={(e) => onValueChange(e.target.value)}
      onBlur={onInputCommit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          onInputCommit();
        }
        if (e.key === 'Escape') {
          onInputCancel();
        }
      }}
    />
  );
}

export function RailLeafChip({
  node,
  path,
  editContext,
  subquery,
  kind = 'where',
}: {
  readonly node: LeafNode;
  readonly path: readonly number[];
  readonly editContext?: VqeEditContext;
  readonly subquery?: RailSubquery;
  readonly kind?: BooleanClauseTarget;
}): ReactElement {
  const pathKey = path.join('-') || 'root';
  const { track } = useAnalytics();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(node.text);
  const inputRef = useRef<HTMLInputElement>(null);

  // Mirror upstream text changes outside of an active edit.
  useEffect(() => {
    if (!editing) setValue(node.text);
  }, [node.text, editing]);

  // Autofocus + select all when entering edit mode.
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  // Subquery-drill branch (3B.7): when the leaf wraps an inline subquery, render
  // the 3C drill chips in place of editable text. Precedes the read-only and
  // editable branches so it applies in both modes; the remove button only shows
  // with an editContext. Mirrors FilterPillRow's subquery branch, including
  // its exception: a leaf that is one chip filter as a whole stays editable.
  const inlineRender =
    subquery && !(editContext && isWholeChipFilter(node.text, editContext))
    ? renderInlineSubqueries({
        bufferText: subquery.bufferText,
        start: node.range.startOffset,
        end: node.range.endOffset,
        options: subquery.options,
        subqueryCtx: subquery.subqueryCtx,
        testIdPrefix: `vqe-${kind}-rail-leaf-${pathKey}`,
        drillLabelPrefix: buildDrillLabel({
          kind,
          identifier: extractPredicateColumn(node.text),
          positionalIndex: path[path.length - 1] ?? 0,
        }),
        editContext,
      })
    : { nodes: null, subqueryCount: 0 };

  if (inlineRender.subqueryCount > 0) {
    return (
      <span
        className={`${BASE_CHIP} relative pr-7`}
        data-testid={`vqe-${kind}-rail-leaf`}
        data-path={pathKey}
        data-negated={node.negated ? 'true' : 'false'}
        data-has-subquery="true"
      >
        {node.negated ? <span className="text-[10px] text-muted-foreground">NOT</span> : null}
        <span
          className="flex flex-wrap items-center gap-1"
          data-testid={`vqe-${kind}-rail-leaf-${pathKey}-text`}
        >
          {inlineRender.nodes}
        </span>
        {editContext ? (
          <span className="absolute right-1 top-1">
            <RemoveButton
              testId={`vqe-${kind}-rail-leaf-${pathKey}-remove`}
              ariaLabel="Remove condition"
              disabled={false}
              onClick={() => {
                void removeFilterNode(path, toCommitCtx(editContext), kind);
                track(`feature:vqe_${kind}_node_edit`, { action: 'remove' });
              }}
            />
          </span>
        ) : null}
      </span>
    );
  }

  // Read-only branch: byte-identical to the 3B.2 LeafChip output.
  if (!editContext) {
    return (
      <span
        className={BASE_CHIP}
        data-testid={`vqe-${kind}-rail-leaf`}
        data-path={pathKey}
        data-negated={node.negated ? 'true' : 'false'}
      >
        {node.negated ? (
          <span className="text-[10px] text-muted-foreground">NOT</span>
        ) : null}
        <span className="text-[12px]">{node.text}</span>
      </span>
    );
  }

  const commit = (): void => {
    const trimmed = value.trim();
    setEditing(false);
    if (trimmed.length === 0 || trimmed === node.text) {
      setValue(node.text);
      return;
    }
    void modifyFilterNode(path, trimmed, toCommitCtx(editContext), kind);
    track(`feature:vqe_${kind}_node_edit`, { action: 'modify' });
  };

  return (
    <span
      className={BASE_CHIP}
      data-testid={`vqe-${kind}-rail-leaf`}
      data-path={pathKey}
      data-negated={node.negated ? 'true' : 'false'}
    >
      {node.negated ? (
        <span className="text-[10px] text-muted-foreground">NOT</span>
      ) : null}
      {editing ? (
        <RailLeafEditor
          editContext={editContext}
          testIdPrefix={`vqe-${kind}-rail-leaf-${pathKey}`}
          text={node.text}
          value={value}
          onValueChange={setValue}
          inputRef={inputRef}
          onChipCommit={(newText) => {
            setEditing(false);
            void modifyFilterNode(path, newText, toCommitCtx(editContext), kind);
            track(`feature:vqe_${kind}_node_edit`, { action: 'modify' });
          }}
          onChipCancel={() => {
            setValue(node.text);
            setEditing(false);
          }}
          onInputCommit={commit}
          onInputCancel={() => {
            setValue(node.text);
            setEditing(false);
          }}
        />
      ) : (
        <button
          type="button"
          data-testid={`vqe-${kind}-rail-leaf-${pathKey}-text`}
          className="font-mono text-[12px]"
          onClick={() => {
            setValue(node.text);
            setEditing(true);
          }}
        >
          {node.text}
          <PillValueLabel text={node.text} />
        </button>
      )}
      <RemoveButton
        testId={`vqe-${kind}-rail-leaf-${pathKey}-remove`}
        ariaLabel="Remove condition"
        disabled={false}
        onClick={() => {
          void removeFilterNode(path, toCommitCtx(editContext), kind);
          track(`feature:vqe_${kind}_node_edit`, { action: 'remove' });
        }}
      />
    </span>
  );
}
